import { useCallback, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Box } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import PropTypes from 'prop-types'
import { Responsive, WidthProvider } from 'react-grid-layout'
import ChartTile from '../tile/chart/ChartTile'
import DatagridTile from '../tile/datagrid/DatagridTile'
import ImageViewerTile from '../tile/image-viewer/ImageViewerTile'
import BrowserTile from '../tile/browser/BrowserTile'
import MapTile from '../tile/map/MapTile'
import PageTile from '../tile/page/PageTile'
import MobileUnitsTile from '../tile/mobile-unit/MobileUnitsTile'
import FeedTile from '../tile/feed/FeedTile'
import DoformsMapTile from '../tile/doFormsMap/DoformsMapTile'
import TextSearchTile from '../tile/text-search/TextSearchTile'
import DoFormsFormTile from '../tile/doforms-form/DoformsFormTile'
import DispatchSchedulerTile from '../tile/dispatch-scheduler/DispatchSchedulerTile'
import DriverEventsTile from '../tile/driver-events/DriverEventsTile'
import DriverDrivesTile from '../tile/driver-drives/DriverDrivesTile'
import JobSummaryTile from '../tile/job-summary/JobSummaryTile'
import EmployeeSummaryTile from '../tile/summaries/employee/EmployeeSummaryTile'

import { JobSummaryTile as JobSummaryTileV2 } from '../tile/summaries/job/JobSummaryTile'
import { TaskSummaryTile } from '../tile/summaries/task/TaskSummaryTile'
import PlottingMapTile from '../tile/plotting-map/PlottingMapTile'
import { isJson } from 'utils/functions/helpers'
import { checkKeyIsExist, domainsWithHttps } from '../tile/utils/helpers'
import { isEqual } from 'lodash'
import { DEFAULT_CHECKED_OPTIONS } from 'components/pages/dis-v2/_components/OtherOptions'
import { DEFAULT_DOFORM_CHECKED_OPTIONS } from '../tile/doforms-form/DoformsForm'
import { MasterDateTimeTile } from '../tile/master-datetime/MasterDatetimeTile'
import { CSP } from 'config'
//import { checkKeyIsExist } from '../utils/helpers'

import { TYPES_OF_TILE } from '../../../../constants'
import { getDefaultMigratedOptionValue } from '../tile/utils/tileOtherOptionMigration'
import { ModalDashboardProvider } from '../tile/modal-dashboard/helper/modalDashboardContext'

const ResponsiveReactGridLayout = WidthProvider(Responsive)

const tileComponents = {
	datagrid: DatagridTile,
	GeoMap: MapTile,
	Page: PageTile,
	Chart: ChartTile,
	FormList: DatagridTile,
	ImageViewer: ImageViewerTile,
	MobileUnits: MobileUnitsTile,
	Feed: FeedTile,
	DoformsMap: DoformsMapTile,
	Browser: BrowserTile,
	TextSearch: TextSearchTile,
	DoFormsForm: DoFormsFormTile,
	MasterDateTime: MasterDateTimeTile,
	Scheduler: DispatchSchedulerTile,
	DriverEvents: DriverEventsTile,
	DriverDrives: DriverDrivesTile,
	JobSummary: JobSummaryTile,
	JobSummaryTile: JobSummaryTileV2,
	TaskSummaryTile: TaskSummaryTile,
	PlottingMap: PlottingMapTile,
	EmployeeSummaryTile: EmployeeSummaryTile,
}

function renderTile(item, columnsByView, setColumnsByView, hiddenTiles) {
	const TileComponent = tileComponents[item?.type]
	return TileComponent ? (
		<TileComponent
			tile={item}
			columnsByView={columnsByView}
			setColumnsByView={setColumnsByView}
			hiddenTiles={hiddenTiles}
		/>
	) : null
}

function DragDropLayout({
	layout,
	isActive = false,
	isOnDashboard = false,
	onLayoutChange = () => {},
	isSettingDialog = false,
	passedIdValue = undefined,
	modalDashboardSetting = undefined,
}) {
	const [columnsByView, setColumnsByView] = useState({})

	const history = useHistory()

	useEffect(() => {
		if (isActive) return

		const combinedDomains = new Set(
			CSP
				? CSP.split(/\s+/)
						.map((d) => d.trim())
						.filter(Boolean)
				: []
		)
		let allowedDomainsStore = []

		try {
			allowedDomainsStore = JSON.parse(localStorage.getItem('allowedDomains')) || []
		} catch {
			allowedDomainsStore = []
		}

		const hasBrowserTile = layout.some((item) => item.type === 'Browser')

		layout.forEach((item) => {
			if (item.type !== 'Browser') return
			if (item.settings) {
				try {
					const settingsObj = JSON.parse(item.settings)
					if (settingsObj.allowedDomains && settingsObj.allowedDomains.length > 0) {
						const domains = settingsObj.allowedDomains
							.split(/\s+/)
							.map((d) => d.trim())
							.filter(Boolean)

						const domainsWithHttpsList = domainsWithHttps(domains)

						domainsWithHttpsList.forEach((domain) => combinedDomains.add(domain))
					}
				} catch (error) {
					console.error('Failed to parse item.settings:', error)
				}
			}
		})

		if (hasBrowserTile) {
			const newDomains = Array.from(combinedDomains)
			if (isEqual([...allowedDomainsStore].sort(), [...newDomains].sort())) return
			localStorage.setItem('allowedDomains', JSON.stringify(newDomains))
			history.go(0)
		}
	}, [layout])

	const [hiddenTiles, setHiddenTiles] = useState([])
	useEffect(() => {
		if (!isOnDashboard || isSettingDialog) return

		const hiddenTilesTemp = {}
		layout.forEach((item) => {
			const parsedSettings = JSON.parse(item.settings)
			switch (item?.type) {
				case TYPES_OF_TILE.BLANK:
					hiddenTilesTemp[item.key] = item
					break
				case TYPES_OF_TILE.DATA_GRID:
				case TYPES_OF_TILE.PLOTTING_MAP:
					const isShowInDashboard = !parsedSettings?.migrateOtherOptions?.includes(
						'showInDashboard'
					)
						? getDefaultMigratedOptionValue({
								key: 'showInDashboard',
								tileType: item.type,
						  })
						: checkKeyIsExist('showInDashboard', parsedSettings?.otherOptionsChecked)
					if (!isShowInDashboard) hiddenTilesTemp[item.key] = item
					break
				case TYPES_OF_TILE.DO_FORMS_FORM:
					if (!checkKeyIsExist('showInDashboard', parsedSettings?.otherOptionsChecked)) {
						hiddenTilesTemp[item.key] = item
					}
					break
				default:
					break
			}
		})

		if (isEqual(hiddenTiles, hiddenTilesTemp)) return
		setHiddenTiles(hiddenTilesTemp)
	}, [layout, isOnDashboard, isSettingDialog])

	const renderItemComponent = useCallback(() => {
		if (!Array.isArray(layout) || layout.length === 0) return null

		const hiddenIds = new Set(Object.keys(hiddenTiles))

		const visibleTiles = layout.filter((item) => !hiddenIds.has(item.key))

		return visibleTiles.map((item) => (
			<Box
				key={isOnDashboard ? item.key : item.i}
				data-grid={item}
				sx={{
					boxShadow: isOnDashboard ? 1 : undefined,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: blueGrey[50],
					'& .react-resizable-handle, & .react-resizable-handle-se': {
						display: isOnDashboard ? 'none !important' : undefined,
					},
				}}
			>
				{isOnDashboard ? renderTile(item, columnsByView, setColumnsByView, hiddenTiles) : item.i}
			</Box>
		))
	}, [isOnDashboard, layout, hiddenTiles, columnsByView, setColumnsByView])

	return (
		<ModalDashboardProvider
			passedIdValue={passedIdValue}
			modalDashboardSetting={modalDashboardSetting}
		>
			<ResponsiveReactGridLayout
				cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
				layouts={{ lg: layout }}
				rowHeight={95}
				onLayoutChange={(newLayout) => onLayoutChange?.(newLayout)}
			>
				{renderItemComponent()}
			</ResponsiveReactGridLayout>
		</ModalDashboardProvider>
	)
}

DragDropLayout.propTypes = {
	layout: PropTypes.array.isRequired,
	isActive: PropTypes.bool,
	isOnDashboard: PropTypes.bool,
	onLayoutChange: PropTypes.func,
	passedIdValue: PropTypes.string,
	modalDashboardSetting: PropTypes.object,
}

export default DragDropLayout
